import type { ArticleShort } from "./article";
import type { DatoImageType } from "../types";
import type { TalkData } from "content-ui/talks/types";
import { SeoData } from "./datoBlocks";
import { BriefingType } from "content-ui/briefings/types";
import type { ArticleFeedSectionData } from "components/feedsSection/types";
import type { PodcastEpisode } from "components/podcastEpisodesSection/types";

export const HOMEPAGE_SECTION_IDS = [
  "topBanner",
  "hero",
  "pro",
  "feedsAndPodcast",
  "newsletters",
  "latest",
  "siftedInsights",
  "events",
  "talks",
] as const;

export type HomepageSectionId = (typeof HOMEPAGE_SECTION_IDS)[number];

export type HomepageSectionOrderItem = {
  id: string;
  text?: string | null;
};

export type CalloutDato = {
  __typename?: "HomeHeroCalloutVariantRecord";
  id: string;
  title: string;
  description?: string;
  buttonLabel?: string;
  buttonCta?: string;
  accent: string;
  buttonVariant?: string;
  createdAt?: string;
};

export type HeroArticleRef = {
  id: string;
  articleId: string;
};

// FeaturedHeroArticleRef is an alias for HeroArticleRef
// Both represent the same reference structure for hero article data
export type FeaturedHeroArticleRef = HeroArticleRef;

export type FeaturedArticle = ArticleShort & {
  __typename: "ArticleRecord";
};

export type FeaturedBriefing = BriefingLinkRecord;

export type HomepageSector = {
  name: string | "All";
  featured: (FeaturedArticle | FeaturedBriefing)[];
};

export type HomepageHeroCarouselItem = {
  title: string;
  link: string;
  description: string;
  cta: string;
  image: DatoImageType;
  imageOnly: boolean | null;
};

export type EventsCarouselItem = {
  id: string;
  url: string;
  mobileImage: DatoImageType;
  desktopTabletImage: DatoImageType;
};

export type DatoShortArticle = {
  __typename: "ArticleRecord";
  title?: string;
  articleId?: string | number;
  articleSlug?: string;
};

export type BriefingLinkRecord = {
  __typename: "BriefingLinkRecord";
  id: string;
  briefingTitle: string;
  briefing: BriefingType;
};

export type FeaturedSector = {
  tabName: "All" | string;
  featuredArticle: (DatoShortArticle | BriefingLinkRecord)[];
};

export type LatestArticlesCategoryFilter = {
  id: string;
  text: string;
};

export type HomepageDatoProps = {
  podcastSectionHeader?: {
    id?: string;
    title?: string;
    cta?: {
      text?: string;
      url?: string;
    };
  };
  talksSectionHeader?: {
    id?: string;
    title?: string;
    cta?: {
      text?: string;
      url?: string;
    };
  };
  sectionsOrder?: HomepageSectionOrderItem[];
  /**
   * SEO
   */
  seo: SeoData["seo"];
  /**
   * Hero section
   */
  heroArticle?: DatoShortArticle | ArticleShort;
  featuredHeroArticles?: (DatoShortArticle | ArticleShort)[];
  latestArticlesCategoriesFilter?: LatestArticlesCategoryFilter[];
  callout?: CalloutDato;
  /**
   * Article Feed section
   */
  popularCta?: {
    url?: string;
    text?: string;
    id?: string;
  };
  popularArticles?: {
    tabOrder?: number;
    tabName?: string;
    featuredArticle?: DatoShortArticle[];
  }[];
  secondTab?: {
    id?: string;
    tabOrder?: number;
    tabName?: string;
    tabCta?: {
      url?: string;
      text?: string;
      id?: string;
    };
  };
  thirdTab?: {
    id?: string;
    tabOrder?: number;
    tabName?: string;
    tabCta?: {
      url?: string;
      text?: string;
      id?: string;
    };
  };
  /**
   * Title shown above the events description block
   */
  descriptionTitle?: string;
  /**
   * Events section
   */
  events: EventsCarouselItem[];
  eventsDescription?: string;
  eventsCta?: {
    id?: string;
    text?: string;
    url?: string;
  };
  /**
   * Header block (from CMS) for the events section
   */
  header?: {
    title?: string;
    cta?: {
      url?: string;
      text?: string;
      updatedAt?: string;
      id?: string;
    };
  };
  /**
   * Newsletter section
   */
  newsletterSectionHeading: string;
  newsletterSubSectionHeading: string;
  newsletterCtaText: string;
  newsletterCtaUrl: string;
  newslettersProSubscribeUrl: string;
  /**
   * Analysis section
   */
  analysisArticles: DatoShortArticle[];
  featuredBriefingsList: BriefingType[];
  /**
   * Podcast Episodes section
   */
  latestEpisodes?: PodcastEpisode[];
  topEpisodes?: PodcastEpisode[];
  podcastCallout?: CalloutDato;
  /**
   * Top Banner fields
   */
  topBannerSignInHeading?: string;
  topBannerSignInSubheading?: string;
  topBannerSignInPrimaryButtonLabel?: string;
  topBannerSignInPrimaryButtonUrl?: string;
  topBannerSignInSecondaryButtonLabel?: string;
  topBannerSignInSecondaryButtonUrl?: string;
  topBannerSignInDaysUntilRedisplay?: number;
  topBannerProHeading?: string;
  topBannerProSubheading?: string;
  topBannerProPrimaryButtonLabel?: string;
  topBannerProPrimaryButtonUrl?: string;
  topBannerProSecondaryButtonLabel?: string;
  topBannerProSecondaryButtonUrl?: string;
  topBannerProDaysUntilRedisplay?: number;
  /**
   * Pro Section
   */
  proSectionTitle?: string;
  proSectionCtaText?: string;
  proSectionCtaLink?: string;
  proCards?: ProCardCMSData[];
  /**
   * Sifted Insights Section
   */
  siftedInsightsTitle?: string;
  featureCards?: ProCardCMSData[];
  insightsFeaturedArticles?:
    | {
        id: string;
        tabName?: string;
        featuredArticle?: HeroArticleRef;
      }[]
    | ArticleShort[];
  /**
   * Latest Items Section
   */
  latestHeader?: LatestHeaderCMS;
  latestItems?: LatestItemCMS[];
};

export type ProCardCMSData = {
  id?: string;
  variant?: "sifted" | "pro";
  headline: string;
  description: string;
  date?: string;
  cardImage: DatoImageType;
  ctaButtonText?: string;
  ctaButtonLink?: string;
};

export type LatestHeaderCMS = {
  title?: string;
  id: string;
  cta?: {
    text?: string;
    url?: string;
    id: string;
  };
};

export type LatestItemCMS = {
  button?: {
    id: string;
    text?: string;
    url?: string;
  };
  title?: string;
  description?: string;
  id: string;
  image?: DatoImageType;
};

export type HomepageProps = {
  sectors: HomepageSector[];
  latestNews?: ArticleShort[];
  latest?: ArticleShort[];
  analysis?: ArticleShort[];
  talks?: TalkData[];
  feedsSectionData?: ArticleFeedSectionData;
  dato: {
    homepage: HomepageDatoProps;
  };
};
